import React, {useRef, useState, useEffect, Suspense, useContext} from "react";
import MuiToolbar from "@material-ui/core/Toolbar";
import {Button, TextField} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import {FuseAnimate, ErrorScreen, AuthContext} from "../../components";
import Toolbar from "@material-ui/core/Toolbar";
import {ReactComponent as PhoneThruLogo} from "../../images/phonethru-logo.svg";
import {isValidMobile} from "../../utils/valid";
import ausPhone from "../../utils/aus-phone";
import {Controller, useForm} from "react-hook-form";
import Typography from "@material-ui/core/Typography";
import Card from "@material-ui/core/Card";
import PasswordStrengthMeter from "../../components/password-strength-meter/password-strength-meter";
import Loading from "../../components/loading/loading";
import {CheckCircleOutline} from "@material-ui/icons";
import {Link} from "react-router-dom";
import e164ify from "../../utils/e164";
import {SignupService} from "../../services";
import mobileLogin from "../../utils/mobile-login";


// NewPasswordField uses the zxcvbn library to check password strength is a large library as it contains password dictionaries,
// so we lazy-load it to enable code splitting and minimize load time
const NewPasswordField = React.lazy(() => import('../../components/new-password-field/new-password-field'))

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection : 'column',
        minHeight: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    toolbar: {
        background: 'linear-gradient(45deg, ' + theme.palette.primary.main + ' 30%, ' + theme.palette.primary.dark + ' 90%)',
        // background: 'linear-gradient(45deg, ' + theme.palette.secondary.main + ' 30%, ' + theme.palette.secondary.dark + ' 90%)',
        // background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
        //backgroundColor: 'white',
        paddingRight: 6,
        paddingLeft: 10,
    },
    logo: {
        width: 122,
        height: 24,
    },
    pad: {
        marginBottom: theme.spacing(2),
    },
    formField: {
        marginBottom: 24,
    },
}));

// Reducer for local storage
const signupReducer = (state, action) => {
    switch (action.type) {
        case 'update':
            return {
                ...state,
                account: {
                    ...state.account,
                    ...action.payload.account,
                },
                signUp: {
                    ...state.signUp,
                    ...action.payload.signUp,
                }
            }
        default:
            return state
    }
}

// Hook for accessing local storage via the reducer
const useReducer = (reducer, initialState) => {
    const [state, setState] = useState(initialState)

    function dispatch(action) {
        const nextState = reducer(state, action)
        setState(nextState)
    }

    return [state, dispatch]
}

const SignUpPage = ({history}) => {

    const classes = useStyles();
    const [state, dispatch] = useReducer(signupReducer, {
        signUp: {
            view: 'edit',           // dialog view: e.g. 'edit', 'success' or 'error'
            stage: 'start',         // dialog flow stage: 'start', 'verify', 'user', 'success', 'error', recovery
            apiError: null,         // API error object
        },
        account: {
            mobileNumber: '',
            verifyId: '',
            verifyCode: '',
            firstName: '',
            lastName: '',
            email: '',
            password: '',
        }
    });

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    return (
        <div className="flex flex-col min-h-full">
            <AppBar position="fixed" className={classes.appBar}>
                <MuiToolbar className={classes.toolbar}>
                    <PhoneThruLogo className={classes.logo} fill="white" />
                </MuiToolbar>
            </AppBar>
            <Toolbar className={classes.pad}/>

            <main className="flex flex-row justify-center">

                <Card className="mx-4 w-full max-w-xl">
                    <div className="p-4">

                        <Typography variant="h6">Create Your Account</Typography>

                        { state.signUp.stage==='start' ?
                            <PhoneNumberForm
                                state={state}
                                dispatch={dispatch}
                            />
                            : null
                        }

                        { state.signUp.stage==='verify' ?
                            <VerifyForm
                                state={state}
                                dispatch={dispatch}
                            />
                            : null
                        }

                        { state.signUp.stage==='user' ?
                            <AccountForm
                                state={state}
                                dispatch={dispatch}
                            />
                            : null
                        }

                        { state.signUp.stage === 'success' ?
                            <SuccessPanel
                                state={state}
                                history={history}
                            />
                            : null
                        }

                        { state.signUp.stage === 'error' && state.signUp.apiError ?
                            <div className="mt-8">
                                <ErrorScreen error={state.signUp.apiError} />
                            </div>
                            : null
                        }

                        { state.signUp.stage === 'recovery' ?
                            <RecoveryPanel state={state}/>
                            : null
                        }

                    </div>
                </Card>

            </main>
        </div>
    );
}

const PhoneNumberForm = ({state, dispatch}) => {
    const focusRef = useRef();
    const classes = useStyles();
    const {handleSubmit, control, formState:{ isValid, isSubmitting }} = useForm({ mode: 'onChange' });


    const onSubmit = (data) => {
        return new Promise((resolve) => {
            SignupService.verifyMobile( e164ify(data.mobileNumber) )
                .then((result) => {
                    // Update the mobile number & verify ID then progress to the verification stage
                    dispatch({
                        type:'update',
                        payload:{
                            account: {
                                mobileNumber: result.address,
                                verifyId: result.verifyId
                            },
                            signUp: {
                                stage: 'verify'
                            },
                        }
                    })
                })
                .catch(error => {
                    // Display an error message
                    dispatch({
                        type:'update',
                        payload: {
                            signUp: {
                                stage: 'error',
                                apiError: error,
                            },
                        }
                    })
                })
                .finally(() => {
                    resolve(); // resolve promise so RHF know's we've finished submitting
                })
        })
    }

    const validateMobile = (value) => {
        return isValidMobile(value) || 'Enter a valid Australian mobile number'
    }

    return (
        <FuseAnimate
            animation="transition.expandIn"
            delay={100}
            complete={() => {
                focusRef.current && focusRef.current.focus();
            }}
        >
            <div>
                <div className="my-6">
                    <Typography paragraph>
                        Enter your mobile phone number.<br/>We'll send your landline calls to this number.
                    </Typography>
                </div>
                <form
                    name="changeMobileForm"
                    noValidate
                    className="flex flex-col justify-center, w-full"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Controller
                        name='mobileNumber'
                        control={control}
                        defaultValue={state.account.mobileNumber}
                        rules={{
                            required: 'A mobile number is required',
                            validate: (value) => validateMobile(value),
                        }}
                        render={ ({field: {onChange, value}, fieldState:{error} }) => (
                            <TextField
                                label="Mobile Number"
                                inputRef={focusRef}
                                type="tel"
                                value={value}
                                onChange={onChange}
                                variant="outlined"
                                error={!!error}
                                helperText={error ? error.message : 'Enter an Australian mobile phone number'}
                                className={classes.formField}
                                required
                                fullWidth
                            />
                        )}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        aria-label="NEXT"
                        disabled={!isValid || isSubmitting}
                    >
                        {isSubmitting ? 'Sending code...' : 'NEXT'}
                    </Button>

                </form>
            </div>
        </FuseAnimate>
    )
}

const VerifyForm = ({state, dispatch}) => {
    const focusRef = useRef();
    const classes = useStyles();
    const {handleSubmit, control, formState:{ isValid, isSubmitting }} = useForm({ mode: 'onChange' });
    const [verifyState, setVerifyState] = useState({error: ''})

    const onSubmit = (data) => {
        return new Promise((resolve) => {
            SignupService.confirmMobile(state.account.verifyId, data.verifyCode)
                .then(result => {
                    // An invalid attempt with return a 200 OK so check error field in response for these codes:
                    // - expired, too_many_attempts, invalid_attempt, duplicate_account
                    if (!!result.error) {
                        if (result.error === 'duplicate_account') {
                            // Go to the recovery stage
                            dispatch({
                                type:'update',
                                payload:{
                                    account: {
                                        verifyCode: data.verifyCode,
                                    },
                                    signUp: {
                                        stage: 'recovery'
                                    },
                                }
                            })

                        } else {
                            setVerifyState({error: result.error})
                        }

                    } else {
                        // Success - move on to collect user details
                        dispatch({
                            type:'update',
                            payload:{
                                account: {
                                    verifyCode: data.verifyCode,
                                },
                                signUp: {
                                    stage: 'user'
                                },
                            }
                        })
                    }

                })
                .catch(error => {
                    // Display an API error message
                    dispatch({
                        type:'update',
                        payload: {
                            signUp: {
                                stage: 'error',
                                apiError: error,
                            },
                        }
                    })
                })
                .finally(() => {
                    resolve() // Resolve the promise so RHF knows we're done.
                })
        })
    }

    const startOver = () => {
        dispatch({
            type:'update',
            payload:{
                signUp: {
                    view: 'edit',
                    stage: 'start',
                    apiError: null,
                },
                account: {
                    mobileNumber: '',
                    verifyId: '',
                    verifyCode: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    password: '',
                }
            }
        })
    }

    return (
        <FuseAnimate
            animation="transition.expandIn"
            delay={100}
            complete={() => {
                focusRef.current && focusRef.current.focus();
            }}
        >
            <div>
                <div className="my-6">
                    { verifyState.error === '' ?
                        <Typography paragraph>
                            We've sent a verification code
                            to <span className="whitespace-nowrap">
                            <strong>{ausPhone(state.account.mobileNumber)}</strong></span>. Enter
                            the code here to continue.
                        </Typography>
                        : null
                    }
                    { verifyState.error === 'invalid_attempt' ?
                        <Typography paragraph>
                            <span className="text-red-400">
                                <strong>The verification code was incorrect.</strong>
                            </span>
                            <br/><br/>
                            Check the code and try again - you have a limited number of tries!
                        </Typography>
                        : null
                    }
                    { verifyState.error === 'too_many_attempts' ?
                        <>
                            <Typography paragraph>
                                <span className="text-red-400">
                                    <strong>The verification code was incorrect.</strong>
                                </span>
                            </Typography>
                            <Typography paragraph>
                                 There have been too many unsuccessful tries for this code and it has been disabled.
                            </Typography>
                            <Typography paragraph>
                                You'll need to start again with a new code, sorry.
                            </Typography>
                        </>
                        : null
                    }
                    { verifyState.error === 'expired' ?
                        <>
                            <Typography paragraph>
                                <span className="text-red-400">
                                    <strong>The verification code has expired.</strong>
                                </span>
                            </Typography>
                            <Typography paragraph>
                                For security, verification codes are only valid for a limited time and this one is too old.
                            </Typography>
                            <Typography paragraph>
                                You'll need to start again with a new code, sorry.
                            </Typography>
                        </>
                        : null
                    }
                </div>
                <form
                    name="verifyCodeForm"
                    noValidate
                    className="flex flex-col justify-center, w-full"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Controller
                        name='verifyCode'
                        control={control}
                        defaultValue={state.account.verifyCode}
                        rules={{
                            required: 'The verification code is required',
                            maxLength: {value: 6, message: 'Your code is too long'},
                            minLength: {value: 6, message: 'Please enter the complete 6 digit code'},
                        }}
                        maxLength={6}
                        render={ ({field: {onChange, value}, fieldState:{error} }) => (
                            <TextField
                                label="Verification Code"
                                inputRef={focusRef}
                                type="tel"
                                value={value}
                                onChange={onChange}
                                variant="outlined"
                                error={!!error}
                                helperText={error ? error.message : 'Enter your verification code'}
                                className={classes.formField}
                                inputProps={{maxLength: 6}}
                                required
                                fullWidth
                            />
                        )}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        aria-label="SEND CODE"
                        disabled={!isValid || isSubmitting || verifyState.error === 'too_many_attempts' || verifyState.error === 'expired'}
                    >
                        {isSubmitting ? 'Sending...' : 'Next'}
                    </Button>

                    <div className="flex flex-col mt-8">
                        <Button color="primary" aria-label="START OVER" onClick={ () => {startOver()}}>
                            Start Over
                        </Button>
                    </div>
                </form>
            </div>
        </FuseAnimate>
    )

}

const AccountForm = ({state, dispatch}) => {
    const classes = useStyles();
    const {handleSubmit, control, formState:{isValid, isSubmitting}} = useForm({ mode: 'onChange' });
    const [scoreState, setScoreState] = useState({pwCheck:{
            score      : 0,
            rating     : '',
            warning    : '',
            suggestions: [
                'Use a few words, avoid common phrases',
                'No need for symbols, digits, or uppercase letters',
            ],
        },
    })

    // Cache the password score so we can update it for password validation in the same render cycle
    let pwScore = scoreState.pwCheck.score

    // onSubmit is called by ReactHookForm when the form is submitted and valid.
    // It returns a promise that is used by RHF to set the isSubmitting flag.
    const onSubmit = (data) => {
        return new Promise((resolve) => {
            // Build API request data
            const request = {
                firstName:      data.firstName,
                lastName:       data.lastName,
                password:       data.password,
                mobileNumber:   state.account.mobileNumber,
                mobileVerifyId: state.account.verifyId,
            }
            SignupService.createAccount(request)
                .then(() => {
                    // Account has been created display success message
                    dispatch({
                        type:'update',
                        payload:{
                            account: {
                                firstName: data.firstName,
                                lastName: data.lastName,
                                password: data.password,
                            },
                            signUp: {
                                stage: 'success'
                            }
                        }
                    })

                })
                .catch(error => {
                    if (error.status === 409) {
                        // Go to the recovery stage
                        dispatch({
                            type:'update',
                            payload:{
                                account: {
                                    verifyCode: data.verifyCode,
                                },
                                signUp: {
                                    stage: 'recovery'
                                },
                            }
                        })
                    } else {
                        // Display an API error message
                        dispatch({
                            type:'update',
                            payload: {
                                signUp: {
                                    stage: 'error',
                                    apiError: error,
                                },
                            }
                        })
                    }
                })
                .finally(() => {
                    resolve() // Resolve the promise so RHF knows we're done.
                })

        })

    }

    const handlePasswordChange = (event, check, onChange) => {
        setScoreState({...scoreState, pwCheck: check})
        pwScore = check.score
        return onChange(event)
    }

    const isValidPassword = (value) => {
        return pwScore > 2
    }

    return(
        <FuseAnimate animation="transition.expandIn" delay={200}>
            <div id="personal-details" className="my-4">
                <Typography variant="h6" color="secondary">Personal Details</Typography>
                <form
                    name="accountForm"
                    noValidate
                    className="flex flex-col justify-center w-full mt-2"
                    onSubmit={handleSubmit(onSubmit)}
                >

                    <Controller
                        name="firstName"
                        control={control}
                        defaultValue=""
                        rules={{required: 'First name is required'}}
                        render={ ({field: {onChange, value}, fieldState:{error} }) => (
                            <TextField
                                label="First Name"
                                variant="outlined"
                                value={value}
                                onChange={onChange}
                                error={!!error}
                                helperText={error ? error.message : null}
                                className={classes.formField}
                                required
                            />
                        )}
                    />

                    <Controller
                        name="lastName"
                        control={control}
                        defaultValue=""
                        rules={{required: 'Last name is required'}}
                        render={ ({field: {onChange, value}, fieldState:{error} }) => (
                            <TextField
                                label="Last Name"
                                variant="outlined"
                                value={value}
                                onChange={onChange}
                                error={!!error}
                                helperText={error ? error.message : null}
                                className={classes.formField}
                                required
                            />
                        )}
                    />

                    <Suspense fallback={<Loading/>}>
                        <Controller
                            name="password"
                            control={control}
                            defaultValue=""
                            rules={{
                                required: 'A password is required',
                                validate: (value) => isValidPassword(value) || 'A strong password is required'
                            }}
                            render={ ({field: {onChange, value}, fieldState:{error} }) => (
                                <>
                                <NewPasswordField
                                    label="Password"
                                    error={!!error}
                                    helperText={error ? error.message : 'Password Strength'}
                                    password={value}
                                    onChange={(event, check) => handlePasswordChange(event, check, onChange)}
                                    exclusions={['prisonconnect','telephoneer','phonethru']} // exclude from passwords
                                />
                                <PasswordStrengthMeter check={scoreState.pwCheck} variant="full"/>
                                </>
                            )}
                        />
                    </Suspense>

                    <div className="mt-2">
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            aria-label="NEXT"
                            className="w-full"
                            disabled={!isValid || isSubmitting}
                        >
                            {isSubmitting ? 'Creating account...' : 'Create my Account'}
                        </Button>
                    </div>

                </form>
            </div>
        </FuseAnimate>
    )
}

const SuccessPanel = ({state, history}) => {
    // authState from AuthContext for login functionality
    const auth = useContext(AuthContext)

    const handleClick = (redirect) => {
        // Login and redirect to the add-service page if we clicked the add service button
        const login = mobileLogin(state.account.mobileNumber);
        auth.login(login, state.account.password, true)
            .then(() => {
                // We've logged in
                if (redirect !== '') {
                    // Navigate to the url
                    history.push(redirect);
                }

            })
            .catch(error => {
                // Set the error message.
                //console.warn('login failed. error:', error)
            })
    }

    return (
        <div className="flex flex-col items-center w-full">
            <FuseAnimate animation="callout.bounce" delay={400}>
                <div className="h-16 my-6">
                    <CheckCircleOutline style={{fontSize: 64, color: 'green'}} />
                </div>
            </FuseAnimate>
            <FuseAnimate animation="transition.expandIn" delay={100}>
                <Typography variant="body1" align="left" paragraph>
                    Your account has been created. You can add a landline number to your account now or just take a look around and add a number later.
                </Typography>
            </FuseAnimate>
            <div className="mt-4 w-full">
                <Button
                    variant="contained"
                    color="primary"
                    aria-label="NEXT"
                    className="w-full"
                    onClick={() => handleClick('/add-service')}
                >
                    ADD A LANDLINE NUMBER
                </Button>
            </div>
            <div className="mt-4 w-full">
                <Button
                    color="primary"
                    aria-label="NEXT"
                    className="w-full"
                    onClick={() => handleClick('')}
                >
                    Take a look around
                </Button>
            </div>
        </div>
    )
}

const RecoveryPanel = ({state}) => {

    return (
        <div className="flex flex-col items-center w-full">
            <FuseAnimate animation="callout.bounce" delay={400}>
                <div className="h-16 my-6">
                    <CheckCircleOutline style={{fontSize: 64, color: 'green'}} />
                </div>
            </FuseAnimate>
            <FuseAnimate animation="transition.expandIn" delay={100}>
                <Typography paragraph>
                    We found an existing account for <span className="whitespace-nowrap"><strong>{ausPhone(state.account.mobileNumber)}</strong></span>.
                    <br/><br/>
                    If you can't remember your password you can log in using a one-time password (OTP) sent to your mobile.
                </Typography>
            </FuseAnimate>
            <div className="mt-4 w-full">
                <Button variant="contained" color="primary" aria-label="NEXT" className="w-full" component={Link} to="/login">
                    GO TO LOGIN
                </Button>
            </div>
        </div>
    )
}


export default SignUpPage;